import React from 'react';
import { Link } from 'react-router-dom';
import SectionHeader from '../components/ui/SectionHeader';
import FinalCTA from '../components/home/FinalCTA';
interface Value {
  icon: string;
  title: string;
  desc: string;
}

const values: Value[] = [
  { icon: '🎯', title: 'Precision over volume', desc: 'We build for accuracy and traceability, not feature quantity. Every disclosure Actula produces should be defensible to an auditor.' },
  { icon: '🔗', title: 'Integration, not duplication', desc: 'Data collected for one obligation should serve many. We eliminate redundant collection — the same energy data should not need to be entered three times.' },
  { icon: '🏢', title: 'Enterprise-grade from day one', desc: 'Role-based access, full audit trails, multi-entity consolidation, and security controls are architectural requirements, not afterthoughts.' },
  { icon: '🌍', title: 'Regulatory depth', desc: 'We go deep on the frameworks we support. BRSR is not a checkbox — it is nine principles, two indicator types, and a multi-entity roll-up. We build accordingly.' },
  { icon: '🤝', title: 'Client partnership', desc: 'Our clients are building new reporting capabilities under real regulatory pressure. We work with them to configure, onboard, and deliver — not hand over software and walk away.' },
  { icon: '📐', title: 'Structured thinking', desc: 'ESG data has structure. Emissions have calculation methodologies. Disclosures have schemas. We treat sustainability data the way financial data is treated — with rigour.' },
];

export default function About(): React.ReactElement {
  return (
    <main>
      <div className="page-hero">
        <div className="container">
          <span className="eyebrow" style={{ color: '#67E8F9', justifyContent: 'center', display: 'flex' }}>About Actula</span>
          <h1>Built for enterprises that cannot afford to get ESG reporting wrong.</h1>
          <p>
            Actula is the ESG data infrastructure platform built for enterprises navigating BRSR, CSRD,
            EU Taxonomy, CBAM, and Scope 3 obligations. We help procurement, sustainability, and finance
            teams collect structured, traceable data &mdash; and produce disclosure-ready outputs.
          </p>
        </div>
      </div>

      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '64px', alignItems: 'center' }} className="about-mission-grid">
            <div>
              <span className="eyebrow">Our mission</span>
              <h2 style={{ color: 'var(--navy)', fontSize: 'clamp(1.5rem, 3vw, 2rem)', lineHeight: 1.3, marginBottom: '20px' }}>
                Make structured ESG data collection as reliable as financial reporting.
              </h2>
              <p style={{ color: 'var(--slate-500)', lineHeight: 1.75, marginBottom: '16px' }}>
                For decades, financial data in large enterprises has been collected in structured systems,
                with defined ownership, approval workflows, and audit trails. ESG data has not had the same
                infrastructure. Sustainability teams have been managing BRSR, CSRD, and Scope 3 obligations
                out of spreadsheets &mdash; with the associated errors, gaps, and year-end scrambles.
              </p>
              <p style={{ color: 'var(--slate-500)', lineHeight: 1.75 }}>
                Actula exists to close that gap. Not by adding another reporting tool, but by building the
                data collection infrastructure that structured ESG disclosure requires.
              </p>
            </div>
            <div style={{ background: 'var(--slate-50)', borderRadius: '16px', padding: '40px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {[
                  { label: 'Regulatory frameworks supported', value: 'BRSR &middot; CSRD &middot; EU Taxonomy &middot; CBAM' },
                  { label: 'Platform modules', value: '5 integrated modules' },
                  { label: 'Data philosophy', value: 'Collect once. Report everywhere.' },
                  { label: 'Deployment', value: 'Cloud-based &middot; EU data residency available' },
                ].map((item, i) => (
                  <div key={i} style={{ borderBottom: i < 3 ? '1px solid var(--slate-200)' : 'none', paddingBottom: i < 3 ? '20px' : '0' }}>
                    <p style={{ color: 'var(--slate-400)', fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{item.label}</p>
                    <p style={{ color: 'var(--navy)', fontWeight: 600, fontSize: '1rem' }} dangerouslySetInnerHTML={{ __html: item.value }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--slate-50)' }}>
        <div className="container">
          <SectionHeader
            eyebrow="Why we exist"
            title="The ESG data infrastructure problem."
            description="Regulatory frameworks like CSRD, BRSR, and EU Taxonomy are not just reporting requirements — they are data requirements. They demand structured, traceable, entity-level data with evidence. Most enterprises do not have the infrastructure to collect it."
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '12px' }}>
            {[
              { problem: 'CSRD requires double materiality assessment linked to ESRS disclosures, with assurance. Spreadsheets cannot provide the traceability assurance requires.' },
              { problem: 'BRSR requires data disaggregated by plant, entity, and principle — collected from hundreds of operational teams and rolled up for the annual report.' },
              { problem: 'EU Taxonomy demands activity-level revenue, CapEx, and OpEx with documented Technical Screening Criteria assessments. This is not a year-end exercise.' },
              { problem: 'CBAM requires quarterly embedded carbon declarations from import data — meaning supplier carbon collection must be structured and repeatable.' },
              { problem: 'Scope 3 Category 1 requires actual supplier emission data, not just spend proxies. Collecting it requires a supplier portal and structured data requests.' },
              { problem: 'All of these frameworks overlap. Energy data is needed for BRSR, ESRS E1, and CCF. Supplier carbon data is needed for CBAM, Scope 3, and PCF. Yet most organisations collect it separately for each.' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'var(--white)', borderRadius: '10px', padding: '24px', borderLeft: '3px solid var(--teal)' }}>
                <p style={{ color: 'var(--slate-500)', lineHeight: 1.65, fontSize: '0.9375rem' }}>{item.problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <SectionHeader
            eyebrow="What we believe"
            title="Our values."
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {values.map((v, i) => (
              <div key={i} className="card">
                <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '12px' }}>{v.icon}</span>
                <h4 style={{ color: 'var(--navy)', marginBottom: '8px' }}>{v.title}</h4>
                <p style={{ color: 'var(--slate-500)', fontSize: '0.9375rem', lineHeight: 1.65 }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--navy)' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '680px' }}>
          <span className="eyebrow" style={{ color: '#67E8F9', justifyContent: 'center', display: 'flex' }}>Get in touch</span>
          <h2 style={{ color: 'var(--white)', marginBottom: '16px' }}>Talk to the Actula team.</h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.75, marginBottom: '32px' }}>
            Whether you are scoping a BRSR implementation, working through CSRD readiness, or trying to
            get CBAM declarations structured before the next reporting cycle, we want to hear about your
            situation specifically.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary">Book a demo</Link>
            <a href="mailto:hello@esgtriage.com" className="btn btn-ghost-light">hello@esgtriage.com</a>
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
